import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  AlertTriangle,
  BadgeCheck,
  BarChart2,
  BarChart3,
  Briefcase,
  Bell,
  BookOpen,
  Boxes,
  Building2,
  CalendarDays,
  ChevronDown,
  ClipboardCheck,
  ClipboardList,
  ClipboardPen,
  CreditCard,
  FilePlus2,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  Gift,
  GitBranch,
  GraduationCap,
  History,
  SlidersHorizontal,
  IdCard,
  Inbox,
  KeyRound,
  Landmark,
  LayoutDashboard,
  ListChecks,
  ListFilter,
  Package,
  PackageCheck,
  PackageSearch,
  RadioTower,
  Scale,
  Settings,
  Shield,
  ShieldAlert,
  ShoppingCart,
  Smartphone,
  Star,
  Tag,
  ThumbsUp,
  Trash2,
  TrendingDown,
  UploadCloud,
  UserCheck,
  UserMinus,
  UserSearch,
  Users,
  Wallet,
  WalletCards,
  Workflow,
  X,
} from "lucide-react";

import logo from "#/assets/eatta-logo.png";
import { useAuth } from "#/context/AuthProvider";

type SidebarProps = {
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
};

type NavItem = {
  label: string;
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  visible: boolean;
  end?: boolean;
};

type NavGroup = {
  title: string;
  baseRoute?: string;
  items: NavItem[];
};

function formatDisplayName(value?: string | null) {
  if (!value) return "User";

  return value
    .split("@")[0]
    .replace(/\./g, " ")
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

export function getModuleDescription(pathname: string) {
  if (pathname.startsWith("/procurement")) {
    return { title: "Procurement", description: "" };
  }

  if (pathname.startsWith("/hr/leave")) {
    return {
      title: "Leave Management",
      description: "",
    };
  }

  if (pathname.startsWith("/employees")) {
    return {
      title: "People Management",
      description: "",
    };
  }

  if (pathname.startsWith("/admin/users")) {
    return {
      title: "User Administration",
      description: "",
    };
  }

  if (pathname.startsWith("/admin/roles")) {
    return {
      title: "Role Management",
      description: "",
    };
  }

  if (pathname.startsWith("/admin/permissions")) {
    return {
      title: "Permissions",
      description: "",
    };
  }

  if (pathname.startsWith("/admin/business-cards")) {
    return {
      title: "Business Card",
      description: "",
    };
  }

  if (pathname.startsWith("/admin/calendar")) {
    return {
      title: "Company Calendar",
      description: "",
    };
  }

  if (pathname.startsWith("/admin/notices")) {
    return {
      title: "Notice Board",
      description: "",
    };
  }

  if (pathname.startsWith("/admin/audit-log")) {
    return {
      title: "Audit Log",
      description: "",
    };
  }

  if (pathname.startsWith("/admin/workflows")) {
    return {
      title: "Workflow Engine",
      description: "",
    };
  }

  if (pathname.startsWith("/admin/departments")) {
    return {
      title: "Departments",
      description: "",
    };
  }

  if (pathname.startsWith("/claims")) {
    return {
      title: "Claims",
      description: "",
    };
  }

  if (pathname.startsWith("/approvals")) {
    return {
      title: "Approvals Inbox",
      description: "",
    };
  }

  if (pathname.startsWith("/inventory")) {
    return {
      title: "Inventory",
      description: "",
    };
  }

  if (pathname.startsWith("/assets")) {
    return {
      title: "Asset Management",
      description: "",
    };
  }

  if (pathname.startsWith("/payroll")) {
    return {
      title: "Finance",
      description: "",
    };
  }

  if (pathname.startsWith("/hr/requisitions")) {
    return {
      title: "Staff Requisitions",
      description: "",
    };
  }

  if (pathname.startsWith("/hr/membership")) {
    return {
      title: "Membership",
      description: "",
    };
  }

  if (pathname.startsWith("/notifications")) {
    return {
      title: "Notifications",
      description: "",
    };
  }

  if (pathname.startsWith("/my-documents")) {
    return {
      title: "My Documents",
      description: "",
    };
  }

  if (pathname.startsWith("/documents")) {
    return {
      title: "Documents",
      description: "",
    };
  }

  if (pathname.startsWith("/hr/training")) {
    return {
      title: "Training",
      description: "",
    };
  }

  if (pathname.startsWith("/hr/airtime")) {
    return {
      title: "Airtime Management",
      description: "",
    };
  }

  return {
    title: "Dashboard",
    description: "",
  };
}

function isRouteActive(pathname: string, item: NavItem) {
  if (item.end) {
    return pathname === item.to;
  }

  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

function itemClass(isActive: boolean) {
  return [
    "group flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold transition-all duration-200",
    isActive
      ? "bg-emerald-600 text-white shadow-lg shadow-emerald-600/25"
      : "text-slate-700 hover:bg-emerald-50 hover:text-emerald-700",
  ].join(" ");
}

export function Sidebar({ mobileOpen = false, onCloseMobile }: SidebarProps) {
  const location = useLocation();
  const { hasRole, hasAnyRole, hasPermission, hasAnyPermission, currentUser } = useAuth();

  const isAdmin = hasRole("SUPER_ADMIN") || hasAnyRole(["ADMIN"]);

  const canManageUsers =
    isAdmin || hasPermission("HR_USER_VIEW") || hasPermission("HR_USER_MANAGE");

  const canManageEmployees =
    isAdmin ||
    hasPermission("HR_EMPLOYEE_VIEW") ||
    hasPermission("HR_EMPLOYEE_MANAGE");

  const canManageRoles =
    hasRole("SUPER_ADMIN") ||
    hasPermission("HR_ROLE_VIEW") ||
    hasPermission("HR_ROLE_MANAGE");

  const canViewPermissions =
    hasRole("SUPER_ADMIN") ||
    hasPermission("HR_PERMISSION_VIEW") ||
    hasPermission("HR_PERMISSION_MANAGE");

  const canManageBusinessCard =
    hasRole("SUPER_ADMIN") ||
    hasPermission("HR_BUSINESS_CARD_VIEW") ||
    hasPermission("HR_BUSINESS_CARD_MANAGE");

  const canManageCalendar =
    hasRole("SUPER_ADMIN") ||
    hasPermission("HR_CALENDAR_VIEW") ||
    hasPermission("HR_CALENDAR_MANAGE");

  const canManageNotices =
    hasRole("SUPER_ADMIN") ||
    hasPermission("HR_NOTICE_VIEW") ||
    hasPermission("HR_NOTICE_MANAGE") ||
    hasPermission("HR_NOTICE_APPROVE");

  const canViewAuditLog =
    hasRole("SUPER_ADMIN") ||
    hasPermission("HR_AUDIT_VIEW") ||
    hasPermission("HR_AUDIT_MANAGE");

  const canManageWorkflows =
    isAdmin ||
    hasPermission("HR_WORKFLOW_VIEW") ||
    hasPermission("HR_WORKFLOW_MANAGE");

  const canViewLeave =
    isAdmin ||
    hasPermission("HR_LEAVE_VIEW") ||
    hasPermission("HR_LEAVE_APPLY") ||
    hasPermission("HR_LEAVE_APPROVE") ||
    hasPermission("HR_LEAVE_MANAGE");

  const canApplyLeave =
    isAdmin ||
    hasPermission("HR_LEAVE_APPLY") ||
    hasPermission("HR_LEAVE_MANAGE");

  const canApproveLeave =
    isAdmin ||
    hasPermission("HR_LEAVE_APPROVE") ||
    hasPermission("HR_LEAVE_MANAGE");

  const canManageLeave = isAdmin || hasPermission("HR_LEAVE_MANAGE");

  const canApproveClaims =
    hasPermission("HR_CLAIM_APPROVE") ||
    hasPermission("HR_CLAIM_MANAGE") ||
    hasPermission("EXPENSE_APPROVE") ||
    hasPermission("FINANCE_CLAIM_APPROVE");

  const canMarkClaimPaid =
    isAdmin ||
    hasPermission("HR_CLAIM_APPROVE") ||
    hasPermission("FINANCE_CLAIM_APPROVE");

  // HR_CLAIM_VIEW_ALL (V87): view all employees' claims + org-wide stats
  const canViewAllClaims = isAdmin || hasPermission("HR_CLAIM_VIEW_ALL");

  const canViewInventory =
    isAdmin ||
    hasPermission("HR_INVENTORY_VIEW") ||
    hasPermission("HR_INVENTORY_CREATE") ||
    hasPermission("HR_INVENTORY_EDIT") ||
    hasPermission("HR_INVENTORY_MANAGE") ||
    hasPermission("HR_INVENTORY_APPROVE") ||
    hasPermission("HR_INVENTORY_ISSUE") ||
    hasPermission("HR_INVENTORY_IMPORT_MANAGE");

  const canManageInventory =
    isAdmin ||
    hasPermission("HR_INVENTORY_MANAGE") ||
    hasPermission("HR_INVENTORY_EDIT");


  const canIssueInventory = isAdmin || hasPermission("HR_INVENTORY_ISSUE");

  const canImportInventory =
    isAdmin || hasPermission("HR_INVENTORY_IMPORT_MANAGE");

  const canViewProcurement = isAdmin || hasAnyPermission(["PROCUREMENT_VIEW", "PROCUREMENT_CREATE", "PROCUREMENT_APPROVE", "PROCUREMENT_MANAGE"]);
  const canManageProcurement = isAdmin || hasAnyPermission(["PROCUREMENT_MANAGE", "PROCUREMENT_CREATE"]);
  const canApproveProcurement = isAdmin || hasAnyPermission(["PROCUREMENT_APPROVE", "PROCUREMENT_MANAGE"]);

  const canViewHrRequisitions =
    isAdmin ||
    hasPermission("HR_REQUISITION_VIEW") ||
    hasPermission("HR_REQUISITION_CREATE") ||
    hasPermission("HR_REQUISITION_EDIT") ||
    hasPermission("HR_REQUISITION_APPROVE");

  const canApproveHrRequisitions =
    isAdmin ||
    hasPermission("HR_REQUISITION_APPROVE") ||
    hasPermission("HR_REQUISITION_PROCUREMENT_PROCESS");

  // Confirmed 2026-07-09 — the Fulfillment Queue's real backend gate is
  // HR_REQUISITION_FULFILL_STOCK (Store Officer), not HR_INVENTORY_MANAGE. That permission was
  // only ever granted to PROCUREMENT_OFFICER (migration V69); STORE_OFFICER never had it until
  // migration V106. Keeping the older checks too since Asset Manager's side of this queue may
  // still rely on ASSET_ASSIGN — only the stock-fulfillment gate was confirmed broken.
  const canFulfillRequisitions =
    isAdmin ||
    hasPermission("HR_REQUISITION_FULFILL_STOCK") ||
    hasPermission("HR_INVENTORY_MANAGE") ||
    hasPermission("ASSET_ASSIGN");

  const canReportRequisitions =
    isAdmin ||
    hasPermission("HR_REQUISITION_MANAGE") ||
    hasPermission("HR_REQUISITION_REPORT");

  const canViewMembership =
    isAdmin ||
    hasPermission("HR_MEMBERSHIP_VIEW") ||
    hasPermission("HR_MEMBERSHIP_MANAGE") ||
    hasPermission("HR_MEMBERSHIP_REPORT_VIEW");

  const canViewMembershipReports =
    isAdmin ||
    hasPermission("HR_MEMBERSHIP_VIEW") ||
    hasPermission("HR_MEMBERSHIP_MANAGE") ||
    hasPermission("HR_MEMBERSHIP_REPORT_VIEW");

  // Matches the permission gate the old "Upload" button on the Membership
  // dashboard used (MEMBERSHIP_PERMISSIONS.CREATE / .MANAGE) -- importing data is a
  // write action, so a report-only or view-only user shouldn't see this entry.
  const canImportMembership =
    isAdmin ||
    hasPermission("HR_MEMBERSHIP_CREATE") ||
    hasPermission("HR_MEMBERSHIP_MANAGE");

  const canManageMembershipConfig =
    isAdmin || hasPermission("HR_MEMBERSHIP_CONFIG_MANAGE") || hasPermission("HR_MEMBERSHIP_MANAGE");

  const canViewAirtime =
    isAdmin ||
    hasPermission("HR_AIRTIME_VIEW") ||
    hasPermission("HR_AIRTIME_MANAGE") ||
    hasPermission("HR_AIRTIME_REPORT_VIEW");

  const canManageAirtime =
    isAdmin ||
    hasPermission("HR_AIRTIME_MANAGE") ||
    hasPermission("HR_AIRTIME_CONFIG_MANAGE");

  const canViewAirtimeReports =
    isAdmin || hasPermission("HR_AIRTIME_REPORT_VIEW");

  const canViewTraining =
    isAdmin ||
    hasAnyPermission([
      "HR_TRAINING_VIEW",
      "HR_TRAINING_CREATE",
      "HR_TRAINING_APPROVE",
      "HR_TRAINING_MANAGE",
    ]);

  const canApplyTraining =
    isAdmin ||
    hasAnyPermission(["HR_TRAINING_CREATE", "HR_TRAINING_MANAGE"]);

  const canApproveTraining =
    isAdmin ||
    hasAnyPermission(["HR_TRAINING_APPROVE", "HR_TRAINING_MANAGE"]);

  const canManageTraining =
    isAdmin || hasPermission("HR_TRAINING_MANAGE");

  // The Reports page's underlying listAll endpoint requires HR_TRAINING_VIEW specifically
  // (not CREATE/APPROVE/MANAGE) — narrower than canViewTraining, so the link isn't shown to
  // someone who'd just get bounced off the page.
  const canViewTrainingReports =
    isAdmin || hasPermission("HR_TRAINING_VIEW");

  const canViewAssets = isAdmin || hasAnyPermission([
    "ASSET_VIEW", "ASSET_CREATE", "ASSET_EDIT", "ASSET_MANAGE",
    "ASSET_ASSIGN", "ASSET_DISPOSE", "ASSET_MAINTENANCE", "ASSET_IMPORT", "ASSET_REPORT_VIEW",
  ]);
  const canAssignAssets        = isAdmin || hasAnyPermission(["ASSET_ASSIGN",        "ASSET_MANAGE"]);
  const canDisposeAssets       = isAdmin || hasAnyPermission(["ASSET_DISPOSE",       "ASSET_MANAGE"]);
  const canApproveDisposals    = isAdmin || hasAnyPermission(["ASSET_DISPOSE_APPROVE","ASSET_MANAGE"]);
  const canImportAssets        = isAdmin || hasAnyPermission(["ASSET_IMPORT",        "ASSET_MANAGE"]);
  const canManageAssets        = isAdmin || hasPermission("ASSET_MANAGE");
  const canReportAssets        = isAdmin || hasAnyPermission(["ASSET_REPORT_VIEW",   "ASSET_MANAGE"]);

  const canViewPayroll    = isAdmin || hasAnyPermission(["HR_PAYROLL_VIEW", "HR_PAYROLL_PROCESS", "HR_PAYROLL_MANAGE", "HR_PAYROLL_APPROVE"]);
  const canManagePayroll  = isAdmin || hasPermission("HR_PAYROLL_MANAGE");

  const canViewDocuments =
    isAdmin ||
    hasAnyPermission([
      "HR_DOCUMENT_VIEW",
      "HR_DOCUMENT_MANAGE",
      "HR_DOCUMENT_UPLOAD",
    ]);

  const groups = useMemo<NavGroup[]>(
    () => [
      {
        title: "Main",
        items: [
          {
            label: "Dashboard",
            to: "/dashboard",
            icon: LayoutDashboard,
            visible: true,
            end: true,
          },
          {
            label: "Notifications",
            to: "/notifications",
            icon: Bell,
            visible: Boolean(currentUser),
            end: true,
          },
          {
            label: "My Documents",
            to: "/my-documents",
            icon: FolderOpen,
            visible: Boolean(currentUser),
            end: true,
          },
          {
            label: "Documents",
            to: "/documents",
            icon: FolderOpen,
            visible: canViewDocuments,
            end: true,
          },
        ],
      },

      {
        title: "Leave Management",
        baseRoute: "/hr/leave",
        items: [
          {
            label: "Leave Dashboard",
            to: "/hr/leave",
            icon: CalendarDays,
            visible: canViewLeave,
            end: true,
          },
          {
            label: "Apply for Leave",
            to: "/hr/leave/requests/new",
            icon: FilePlus2,
            visible: canApplyLeave,
            end: true,
          },
          {
            label: "My Leave",
            to: "/hr/leave/my-leave",
            icon: ClipboardList,
            visible: canViewLeave,
            end: true,
          },
          {
            label: "Approval Inbox",
            to: "/hr/leave/approvals",
            icon: ClipboardCheck,
            visible: canApproveLeave,
            end: true,
          },
          {
            label: "Official Duty",
            to: "/hr/leave/official-duty",
            icon: Briefcase,
            visible: canViewLeave,
            end: true,
          },
          {
            label: "Reports",
            to: "/hr/leave/reports",
            icon: BarChart3,
            visible: canViewLeave,
            end: true,
          },
          {
            label: "Leave Setup",
            to: "/hr/leave/setup",
            icon: Settings,
            visible: canManageLeave,
            end: true,
          },
        ],
      },

      {
        title: "Organisation",
        items: [
          {
            label: "Departments",
            to: "/admin/departments",
            icon: Building2,
            visible: isAdmin,
          },
          {
            label: "Employees",
            to: "/employees",
            icon: IdCard,
            visible: canManageEmployees,
          },
          {
            label: "Users",
            to: "/admin/users",
            icon: Users,
            visible: canManageUsers,
          },
          {
            label: "Roles",
            to: "/admin/roles",
            icon: Shield,
            visible: canManageRoles,
          },
          {
            label: "Permissions",
            to: "/admin/permissions",
            icon: KeyRound,
            visible: canViewPermissions,
            end: true,
          },
          {
            label: "Business Card",
            to: "/admin/business-cards",
            icon: CreditCard,
            visible: canManageBusinessCard,
            end: true,
          },
          {
            label: "Calendar",
            to: "/admin/calendar",
            icon: CalendarDays,
            visible: canManageCalendar,
            end: true,
          },
          {
            label: "Notices",
            to: "/admin/notices",
            icon: Bell,
            visible: canManageNotices,
            end: true,
          },
        ],
      },

      {
        title: "Claims & Approvals",
        baseRoute: "/claims",
        items: [
          {
            label: "Dashboard",
            to: "/claims",
            icon: LayoutDashboard,
            visible: Boolean(currentUser),
            end: true,
          },
          {
            label: "My Claims",
            to: "/claims/mine",
            icon: WalletCards,
            visible: Boolean(currentUser),
            end: true,
          },
          {
            label: "Claims Approval",
            to: "/claims/approvals",
            icon: ClipboardCheck,
            visible: canApproveClaims,
            end: true,
          },
          {
            label: "Paid Claims",
            to: "/claims/payment",
            icon: Wallet,
            visible: canMarkClaimPaid,
            end: true,
          },
          {
            label: "All Claims",
            to: "/claims/admin",
            icon: ListFilter,
            visible: canViewAllClaims,
            end: true,
          },
          {
            label: "Reports",
            to: "/claims/reports",
            icon: BarChart2,
            visible: Boolean(currentUser),
            end: true,
          },
        ],
      },

      {
        title: "Inventory",
        baseRoute: "/inventory",
        items: [
          {
            label: "Inventory Dashboard",
            to: "/inventory",
            icon: Boxes,
            visible: canViewInventory,
            end: true,
          },
          {
            label: "Inventory Items",
            to: "/inventory/items",
            icon: PackageSearch,
            visible: canViewInventory,
            end: true,
          },
          {
            label: "Import Stock",
            to: "/inventory/import",
            icon: FileSpreadsheet,
            visible: canImportInventory,
            end: true,
          },
          {
            label: "Item Categories",
            to: "/inventory/categories",
            icon: Settings,
            visible: canManageInventory,
            end: true,
          },
          {
            label: "Reports",
            to: "/inventory/reports",
            icon: BarChart3,
            visible: canViewInventory,
            end: true,
          },
        ],
      },

      {
        title: "Staff Requisitions",
        baseRoute: "/hr/requisitions",
        items: [
          {
            label: "My Requisitions",
            to: "/hr/requisitions/mine",
            icon: ClipboardList,
            visible: canViewHrRequisitions,
            end: true,
          },
          {
            label: "Request Items",
            to: "/hr/requisitions/catalog",
            icon: ShoppingCart,
            visible: canViewHrRequisitions,
            end: true,
          },
          {
            label: "Requisition Inbox",
            to: "/hr/requisitions/inbox",
            icon: ClipboardCheck,
            visible: canApproveHrRequisitions,
            end: true,
          },
          {
            label: "Fulfillment Queue",
            to: "/hr/requisitions/fulfillment",
            icon: PackageCheck,
            visible: canFulfillRequisitions,
            end: true,
          },
          {
            label: "Reports",
            to: "/hr/requisitions/reports",
            icon: BarChart3,
            visible: canReportRequisitions,
            end: true,
          },
        ],
      },

      {
        title: "Procurement",
        baseRoute: "/procurement",
        items: [
          { label: "Dashboard", to: "/procurement", icon: LayoutDashboard, visible: canViewProcurement, end: true },
          { label: "Requisitions", to: "/procurement/requisitions", icon: ClipboardPen, visible: canViewProcurement },
          { label: "RFQs", to: "/procurement/rfqs", icon: FileText, visible: canManageProcurement },
          { label: "Quotation Evaluation", to: "/procurement/evaluations", icon: Scale, visible: canManageProcurement },
          { label: "Local Purchase Orders", to: "/procurement/lpos", icon: ShoppingCart, visible: canManageProcurement },
          { label: "Goods Received Notes", to: "/procurement/grns", icon: PackageCheck, visible: canManageProcurement },
          { label: "Non-Conformance", to: "/procurement/ncrs", icon: AlertTriangle, visible: canManageProcurement },
          { label: "Suppliers", to: "/procurement/suppliers", icon: UserCheck, visible: canViewProcurement },
          { label: "Prequalification", to: "/procurement/supplier-prequal", icon: ShieldAlert, visible: canManageProcurement, end: true },
          { label: "Supplier Evaluation", to: "/procurement/supplier-eval", icon: Star, visible: canManageProcurement, end: true },
          { label: "Dept Feedback", to: "/procurement/admin/feedback", icon: ThumbsUp, visible: canViewProcurement },
          { label: "Tender Register", to: "/procurement/admin/tenders", icon: BookOpen, visible: canManageProcurement, end: true },
          { label: "Risk Register", to: "/procurement/admin/risks", icon: ShieldAlert, visible: canManageProcurement, end: true },
          { label: "Delistings", to: "/procurement/admin/delistings", icon: UserMinus, visible: canApproveProcurement },
          { label: "Reports", to: "/procurement/reports", icon: BarChart3, visible: canViewProcurement, end: true },
        ],
      },

      {
        title: "Asset Management",
        baseRoute: "/assets",
        items: [
          { label: "Dashboard",    to: "/assets",             icon: Package,         visible: canViewAssets,    end: true },
          { label: "My Assets",    to: "/assets/my-assets",   icon: PackageCheck,    visible: Boolean(currentUser), end: true },
          { label: "All Assets",   to: "/assets/list",        icon: PackageSearch,   visible: canViewAssets,    end: true },
          { label: "Assignments",  to: "/assets/assignments", icon: PackageCheck,    visible: canAssignAssets,  end: true },
          { label: "Disposals",      to: "/assets/disposals",       icon: Trash2,          visible: canDisposeAssets,    end: true },
          { label: "Disposal Inbox", to: "/assets/disposals/inbox", icon: Inbox,           visible: canApproveDisposals, end: true },
          { label: "Reports",      to: "/assets/reports",     icon: BarChart3,       visible: canReportAssets,  end: true },
          { label: "Import",       to: "/assets/import",      icon: FileSpreadsheet, visible: canImportAssets,  end: true },
          { label: "Setup",        to: "/assets/setup",       icon: Settings,        visible: canManageAssets,  end: true },
        ],
      },

      {
        title: "Finance",
        baseRoute: "/payroll",
        items: [
          { label: "Dashboard",           to: "/payroll",                      icon: LayoutDashboard, visible: canViewPayroll,      end: true },
          { label: "Periods",             to: "/payroll/periods",              icon: CalendarDays,    visible: canViewPayroll,      end: true },
          { label: "My Payslips",         to: "/payroll/payslips/mine",        icon: Wallet,          visible: Boolean(currentUser),end: true },
          { label: "My Bank Account",     to: "/payroll/bank-account/mine",    icon: Landmark,        visible: Boolean(currentUser),end: true },
          { label: "Reports",             to: "/payroll/reports",              icon: FileSpreadsheet, visible: canViewPayroll,      end: true },
          { label: "Statutory Config",    to: "/payroll/config/statutory",     icon: Settings,        visible: canManagePayroll,    end: true },
          { label: "Deduction Categories",to: "/payroll/deduction-categories", icon: Tag,             visible: canManagePayroll,    end: true },
          { label: "Employee Deductions", to: "/payroll/employee-deductions",  icon: UserSearch,      visible: canManagePayroll,    end: true },
          { label: "Benefit Categories",  to: "/payroll/benefit-categories",   icon: Gift,            visible: canManagePayroll,    end: true },
          { label: "Employee Benefits",   to: "/payroll/employee-benefits",    icon: Gift,            visible: canManagePayroll,    end: true },
          { label: "Bank Accounts",       to: "/payroll/bank-accounts",        icon: WalletCards,     visible: canManagePayroll,    end: true },
        ],
      },

      {
        title: "Training",
        baseRoute: "/hr/training",
        items: [
          {
            label: "Training Dashboard",
            to: "/hr/training",
            icon: GraduationCap,
            visible: canViewTraining,
            end: true,
          },
          {
            label: "My Requests",
            to: "/hr/training/mine",
            icon: ClipboardList,
            visible: canApplyTraining,
            end: true,
          },
          {
            label: "Approval Inbox",
            to: "/hr/training/inbox",
            icon: ClipboardCheck,
            visible: canApproveTraining,
            end: true,
          },
          {
            label: "Courses",
            to: "/hr/training/courses",
            icon: BookOpen,
            visible: canViewTraining,
            end: true,
          },
          {
            label: "Sessions",
            to: "/hr/training/sessions",
            icon: ListChecks,
            visible: canManageTraining,
            end: true,
          },
          {
            label: "Attendance",
            to: "/hr/training/attendance",
            icon: UserCheck,
            visible: canManageTraining,
            end: true,
          },
          {
            label: "Reports",
            to: "/hr/training/reports",
            icon: BarChart3,
            visible: canViewTrainingReports,
            end: true,
          },
          {
            label: "Programs",
            to: "/hr/training/programs",
            icon: ListChecks,
            visible: canViewTraining,
            end: true,
          },
          {
            label: "Setup",
            to: "/hr/training/setup",
            icon: SlidersHorizontal,
            visible: canManageTraining,
            end: true,
          },
        ],
      },

      {
        title: "Membership",
        baseRoute: "/hr/membership",
        items: [
          {
            label: "Dashboard",
            to: "/hr/membership",
            icon: BadgeCheck,
            visible: canViewMembership,
            end: true,
          },
          {
            label: "Import Data",
            to: "/hr/membership/upload",
            icon: UploadCloud,
            visible: canImportMembership,
            end: true,
          },
          {
            label: "Reports",
            to: "/hr/membership/reports",
            icon: LayoutDashboard,
            visible: canViewMembershipReports,
            end: true,
          },
          {
            label: "Manage",
            to: "/hr/membership/manage",
            icon: LayoutDashboard,
            visible: canViewMembership,
            end: true,
          },
          {
            label: "Config",
            to: "/hr/membership/config",
            icon: LayoutDashboard,
            visible: canManageMembershipConfig,
            end: true,
          },
        ],
      },

      {
        title: "Workflow Engine",
        items: [
          {
            label: "Workflows",
            to: "/admin/workflows",
            icon: GitBranch,
            visible: canManageWorkflows,
            end: true,
          },
          {
            label: "Email Queue",
            to: "/admin/email-queue",
            icon: Bell,
            visible: isAdmin || hasPermission("EMAIL_QUEUE_VIEW"),
            end: true,
          },
        ],
      },

      {
        title: "Airtime",
        baseRoute: "/hr/airtime",
        items: [
          {
            label: "Airtime Dashboard",
            to: "/hr/airtime",
            icon: Smartphone,
            visible: canViewAirtime,
            end: true,
          },
          {
            label: "My Airtime",
            to: "/hr/airtime/my-airtime",
            icon: Smartphone,
            visible: Boolean(currentUser),
            end: true,
          },
          {
            label: "Allocations",
            to: "/hr/airtime/allocations",
            icon: WalletCards,
            visible: canManageAirtime,
            end: true,
          },
          {
            label: "Configuration",
            to: "/hr/airtime/config",
            icon: RadioTower,
            visible: canManageAirtime,
            end: true,
          },
          {
            label: "Disbursements",
            to: "/hr/airtime/batches",
            icon: ClipboardCheck,
            visible: canViewAirtimeReports,
            end: true,
          },
        ],
      },

      {
        title: "Audit Log",
        items: [
          {
            label: "Audit Log",
            to: "/admin/audit-log",
            icon: History,
            visible: canViewAuditLog,
            end: true,
          },
        ],
      },
    ],
    [
      currentUser,
      canViewAuditLog,
      canViewLeave,
      canApplyLeave,
      canApproveLeave,
      canManageLeave,
      canManageEmployees,
      canManageUsers,
      canManageRoles,
      canViewPermissions,
      canManageBusinessCard,
      canManageCalendar,
      canManageNotices,
      canApproveClaims,
      canMarkClaimPaid,
      canViewAllClaims,
      canViewInventory,
      canManageInventory,
      canIssueInventory,
      canImportInventory,
      canViewMembership,
      canViewMembershipReports,
      canImportMembership,
      canManageMembershipConfig,
      canManageWorkflows,
      canViewAirtime,
      canManageAirtime,
      canViewAirtimeReports,
      canViewProcurement,
      canManageProcurement,
      canApproveProcurement,
      canViewHrRequisitions,
      canApproveHrRequisitions,
      canFulfillRequisitions,
      canReportRequisitions,
      canViewTraining,
      canViewTrainingReports,
      canApplyTraining,
      canApproveTraining,
      canManageTraining,
      canViewDocuments,
      canViewAssets,
      canAssignAssets,
      canDisposeAssets,
      canApproveDisposals,
      canImportAssets,
      canManageAssets,
      canReportAssets,
      canViewPayroll,
      canManagePayroll,
    ],
  );

  const displayName = formatDisplayName(
    currentUser?.username || currentUser?.email,
  );

  const activeGroup =
    groups.find((group) => {
      if (group.baseRoute && location.pathname.startsWith(group.baseRoute)) return true;
      return group.items.some(
        (item) => item.visible && isRouteActive(location.pathname, item),
      );
    })?.title ?? "Main";

  const [openGroup, setOpenGroup] = useState(activeGroup);

  useEffect(() => {
    setOpenGroup(activeGroup);
  }, [activeGroup]);

  return (
    <>
      {mobileOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-slate-950/50 backdrop-blur-sm lg:hidden"
          onClick={onCloseMobile}
          aria-label="Close navigation overlay"
        />
      ) : null}

      <aside
        className={[
          "fixed inset-y-0 left-0 z-50 w-64 border-r border-slate-200 bg-white shadow-2xl transition-transform duration-300",
          "lg:sticky lg:top-0 lg:z-auto lg:h-screen lg:translate-x-0 lg:shadow-none",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
        ].join(" ")}
      >
        <div className="flex h-full flex-col">
          <div className="border-b border-slate-200 bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-600 px-3 py-4 text-white">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <div className="rounded-xl bg-white/95 p-1.5 shadow-lg shrink-0">
                  <img
                    src={logo}
                    alt="EATTA EMS"
                    className="h-8 w-8 object-contain"
                  />
                </div>

                <div className="min-w-0">
                  <p className="truncate text-sm font-black tracking-wide">
                    EATTA EMS
                  </p>
                  <p className="truncate text-xs text-emerald-50">
                    Electronic Management System
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={onCloseMobile}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white hover:bg-white/20 lg:hidden"
                aria-label="Close navigation"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-3 rounded-xl bg-white/10 px-3 py-2 ring-1 ring-white/15">
              <p className="truncate text-xs font-black">{displayName}</p>
              <p className="mt-0.5 truncate text-xs text-emerald-50/80">
                {currentUser?.jobTitle || "EATTA Employee"}
              </p>
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto bg-slate-50 px-2 py-3">
            <div className="space-y-2">
              {groups.map((group) => {
                const visibleItems = group.items.filter((item) => item.visible);

                if (!visibleItems.length) return null;

                const isOpen = openGroup === group.title;

                const hasActiveItem = visibleItems.some((item) =>
                  isRouteActive(location.pathname, item),
                );

                return (
                  <section
                    key={group.title}
                    className="rounded-2xl border border-slate-200 bg-white p-1.5 shadow-sm"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setOpenGroup((current) =>
                          current === group.title ? "" : group.title,
                        )
                      }
                      className={[
                        "flex w-full items-center justify-between rounded-xl px-2.5 py-2 text-left transition",
                        hasActiveItem
                          ? "bg-emerald-50 text-emerald-800"
                          : "text-slate-700 hover:bg-slate-50",
                      ].join(" ")}
                    >
                      <span className="text-xs font-bold uppercase tracking-wide">
                        {group.title}
                      </span>

                      <ChevronDown
                        className={[
                          "h-4 w-4 transition-transform duration-200",
                          isOpen ? "rotate-180" : "",
                        ].join(" ")}
                      />
                    </button>

                    {isOpen ? (
                      <div className="mt-2 space-y-1">
                        {visibleItems.map((item) => {
                          const Icon = item.icon;

                          return (
                            <NavLink
                              key={item.to}
                              to={item.to}
                              end={item.end}
                              onClick={() => {
                                // Collapse all other groups — keep only this group open
                                setOpenGroup(group.title);
                                onCloseMobile?.();
                              }}
                              className={() =>
                                itemClass(isRouteActive(location.pathname, item))
                              }
                            >
                              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white/20">
                                <Icon className="h-4 w-4" />
                              </span>
                              <span className="truncate">{item.label}</span>
                            </NavLink>
                          );
                        })}
                      </div>
                    ) : null}
                  </section>
                );
              })}
            </div>
          </nav>

          {/* Developer signature */}
          <div className="shrink-0 border-t border-slate-200 bg-slate-50 px-4 py-2.5 text-center">
            <p className="text-[10px] text-slate-400">
              Developed by{" "}
              <span className="font-semibold text-slate-500">Bryan Philip</span>
            </p>
          </div>
        </div>
      </aside>
    </>
  );
}